/**
 * Session 20 — OCPP 2.0.1 FirmwareStatusNotification outbound (CP → CSMS).
 * Same role as v1.6 but the status enum is wider and the message carries
 * the originating `requestId` from UpdateFirmware.
 */

export type V201FirmwareStatus =
  | 'Downloaded'
  | 'DownloadFailed'
  | 'Downloading'
  | 'DownloadScheduled'
  | 'DownloadPaused'
  | 'Idle'
  | 'InstallationFailed'
  | 'Installing'
  | 'Installed'
  | 'InstallRebooting'
  | 'InstallScheduled'
  | 'InstallVerificationFailed'
  | 'InvalidSignature'
  | 'SignatureVerified';

export interface FirmwareStatusNotificationDomain {
  readonly status: V201FirmwareStatus;
  readonly requestId?: number;
}

export function buildFirmwareStatusNotificationRequest(
  req: FirmwareStatusNotificationDomain,
): Record<string, unknown> {
  return {
    status: req.status,
    ...(req.requestId !== undefined ? { requestId: req.requestId } : {}),
  };
}

/**
 * Local Controller role — PublishFirmwareStatusNotification (LC → CSMS).
 * Reports progress of caching a firmware image published via PublishFirmware.
 * `location` (≥ 1 URI) is required by the schema only when status is
 * `Published`; `requestId` echoes the originating PublishFirmwareRequest.
 */
export type V201PublishFirmwareStatus =
  | 'Idle'
  | 'DownloadScheduled'
  | 'Downloading'
  | 'Downloaded'
  | 'Published'
  | 'DownloadFailed'
  | 'DownloadPaused'
  | 'InvalidChecksum'
  | 'ChecksumVerified'
  | 'PublishFailed';

export interface PublishFirmwareStatusNotificationDomain {
  readonly status: V201PublishFirmwareStatus;
  readonly requestId?: number;
  /** Published locations (URIs). Schema requires ≥ 1 when status is Published. */
  readonly location?: readonly string[];
}

export function buildPublishFirmwareStatusNotificationRequest(
  req: PublishFirmwareStatusNotificationDomain,
): Record<string, unknown> {
  return {
    status: req.status,
    ...(req.requestId !== undefined ? { requestId: req.requestId } : {}),
    ...(req.location !== undefined && req.location.length > 0
      ? { location: [...req.location] }
      : {}),
  };
}
